import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { profileAPI } from '../utils/api';
import { PageHeader } from '../components/layout/UI';
import { useToast, apiError } from '../components/Toast';

const AVATAR_COLORS = [
  '#2E539F','#5CC3BC','#FFC849','#ef4444','#8b5cf6',
  '#10b981','#f97316','#0891b2','#be185d','#065f46',
  '#7c3aed','#b45309','#0f766e','#1d4ed8','#9f1239',
];

export default function ProfilePage() {
  const toast = useToast();
  const { user, setUser } = useAuth();

  const [form, setForm]   = useState({ name:'', email:'', avatar_color:'#2E539F' });
  const [pw, setPw]       = useState({ current:'', next:'', confirm:'' });
  const [profileData, setProfileData] = useState(null);

  const [infoMsg,    setInfoMsg]    = useState('');
  const [infoErr,    setInfoErr]    = useState('');
  const [pwMsg,      setPwMsg]      = useState('');
  const [pwErr,      setPwErr]      = useState('');
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingPw,   setSavingPw]   = useState(false);

  useEffect(() => {
    profileAPI.get().then(r => {
      setProfileData(r.data);
      setForm({
        name:         r.data.name         || '',
        email:        r.data.email        || '',
        avatar_color: r.data.avatar_color || '#2E539F',
      });
    }).catch(e => toast.error(apiError(e, 'Could not load this page.')));
  }, []);

  const initials = form.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    setSavingInfo(true); setInfoMsg(''); setInfoErr('');
    try {
      const r = await profileAPI.update(form);
      if (setUser) setUser(prev => ({ ...prev, ...r.data }));
      setInfoMsg('Profile updated successfully');
    } catch(err) {
      setInfoErr(err.response?.data?.message || 'Could not update profile');
    } finally { setSavingInfo(false); }
  };

  const handlePwSubmit = async (e) => {
    e.preventDefault();
    setPwMsg(''); setPwErr('');
    if (pw.next !== pw.confirm) { setPwErr('New passwords do not match'); return; }
    if (pw.next.length < 8)    { setPwErr('Password must be at least 8 characters'); return; }
    setSavingPw(true);
    try {
      await profileAPI.changePassword({ current_password: pw.current, new_password: pw.next });
      setPwMsg('Password changed successfully');
      setPw({ current:'', next:'', confirm:'' });
    } catch(err) {
      setPwErr(err.response?.data?.message || 'Could not change password');
    } finally { setSavingPw(false); }
  };

  return (
    <div>
      <PageHeader title="My Profile" subtitle="Manage your personal information and account settings" />

      <div className="space-y-5">
        {/* ── Personal Information ─────────────────────────────── */}
        <div className="card">
          <h2 className="font-semibold text-sm uppercase tracking-wider mb-4"
            style={{ color:'var(--text-secondary)' }}>Personal Information</h2>

          <form onSubmit={handleInfoSubmit} className="space-y-4">
            {/* Avatar */}
            <div>
              <label className="label">Avatar</label>
              <div className="flex items-center gap-4 mt-1">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-white text-lg flex-shrink-0 shadow-sm"
                  style={{ background: form.avatar_color }}>
                  {initials}
                </div>
                <div className="flex flex-wrap gap-2">
                  {AVATAR_COLORS.map(c => (
                    <button key={c} type="button"
                      onClick={() => setForm(f => ({ ...f, avatar_color: c }))}
                      className="w-7 h-7 rounded-lg flex-shrink-0 transition-transform hover:scale-110"
                      style={{
                        background: c,
                        outline: form.avatar_color === c ? `3px solid ${c}` : 'none',
                        outlineOffset: 2,
                      }} />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="label">Full Name</label>
              <input className="input" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Your full name" required />
            </div>

            <div>
              <label className="label">Email Address</label>
              <input className="input" type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="your@email.com" required />
            </div>

            <div>
              <label className="label">Role</label>
              <div className="input flex items-center gap-2"
                style={{ background:'var(--surface-alt)', cursor:'default', userSelect:'none' }}>
                <span style={{ color:'var(--text)' }}>{user?.role}</span>
                <span className="text-xs" style={{ color:'var(--text-secondary)' }}>
                  — contact an Admin to change your role
                </span>
              </div>
            </div>

            {infoMsg && (
              <p className="text-sm font-medium text-emerald-500 flex items-center gap-1.5">
                ✓ {infoMsg}
              </p>
            )}
            {infoErr && <p className="text-sm text-red-500">{infoErr}</p>}

            <div className="pt-1">
              <button type="submit" disabled={savingInfo} className="btn-primary flex items-center gap-2 text-sm">
                {savingInfo && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                {savingInfo ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>

        {/* ── Change Password ───────────────────────────────────── */}
        <div className="card">
          <h2 className="font-semibold text-sm uppercase tracking-wider mb-4"
            style={{ color:'var(--text-secondary)' }}>Change Password</h2>

          <form onSubmit={handlePwSubmit} className="space-y-4">
            <div>
              <label className="label">Current Password</label>
              <input className="input" type="password" value={pw.current}
                onChange={e => setPw(p => ({ ...p, current: e.target.value }))}
                placeholder="••••••••" required />
            </div>
            <div>
              <label className="label">New Password</label>
              <input className="input" type="password" value={pw.next}
                onChange={e => setPw(p => ({ ...p, next: e.target.value }))}
                placeholder="Min. 8 characters" required />
            </div>
            <div>
              <label className="label">Confirm New Password</label>
              <input className="input" type="password" value={pw.confirm}
                onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))}
                placeholder="••••••••" required />
            </div>

            {pwMsg && <p className="text-sm font-medium text-emerald-500">✓ {pwMsg}</p>}
            {pwErr && <p className="text-sm text-red-500">{pwErr}</p>}

            <div className="pt-1">
              <button type="submit" disabled={savingPw} className="btn-primary flex items-center gap-2 text-sm">
                {savingPw && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                {savingPw ? 'Updating…' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>

        {/* ── Account Info ──────────────────────────────────────── */}
        <div className="card">
          <h2 className="font-semibold text-sm uppercase tracking-wider mb-4"
            style={{ color:'var(--text-secondary)' }}>Account Information</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Member since</p>
              <p className="font-medium text-sm mt-1" style={{ color:'var(--text)' }}>
                {profileData?.created_at
                  ? new Date(profileData.created_at).toLocaleDateString(undefined,
                      { year:'numeric', month:'long', day:'numeric' })
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Account status</p>
              <span className="inline-flex items-center gap-1.5 mt-1 text-sm font-medium text-emerald-500">
                <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0"/> Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
